<script>
  import Page from '@components/page.svelte'
  import Image from '@components/general/image.svelte'
  import Button from '@components/buttons/button.svelte'
  import Table from '@components/table'
  import FilterBar from '@components/table/filter-bar.svelte'
  import Dropdown from '@components/general/dropdown.svelte'
  import FileDetails from '@components/general/file-details.svelte'
  import ImagePreview from '@components/general/image-preview.svelte'
  import ColorIndicator from '@components/general/color-indicator.svelte'
  import { usePageParam } from '@hooks/use-page-param.svelte'
  import { useSelection } from '@hooks/use-selection.svelte'
  import { useForm, router } from '@inertiajs/svelte'
  import { FileDown, ChevronLeft, CirclePlus, Search, Pencil, Trash2, Eye, EyeOff, CheckCheck, CheckCircle, Lock, Unlock, ExternalLink, Link } from 'lucide-svelte'
  import Tag from '@components/general/tag.svelte'
  import { sortItems } from '@lib/utils'

  let { resources = [], pagination, ids, tags = [] } = $props()

  let selection = $derived(useSelection({ initialItems: resources, allIds: ids }))
  
  let sort = usePageParam({ key: "sort", defaultValue: "newest", items: sortItems })

  let status = usePageParam({ key: "status", defaultValue: "active", items: [
    { value: "active", label: "Status: Active", icon: Eye },
    { value: "inactive", label: "Status: Hidden", icon: EyeOff },
    { value: "all", label: "Status: All", icon: CheckCheck }
  ] })

  let access = usePageParam({ key: "access", defaultValue: "all", items: [
    { value: "free", label: "Access: Free", icon: Unlock },
    { value: "premium", label: "Access: Premium", icon: Lock },
    { value: "all", label: "Access: Any", icon: CheckCheck },
  ] })

  let imagePreview = $state({ open: false, src: null, index: null })

  let deleteForm = useForm()

  function showImagePreview(resource, { index = 0 } = {}) {
    imagePreview.src = resource.cover_image ? [resource.cover_image.url] : []
    imagePreview.index = index
    imagePreview.open = true
  }

  function deleteResource(resource) {
    if (!window.confirm("Are you sure you want to delete this resource?")) return
    $deleteForm.delete(`/admin/resources/${resource.id}`)
  }

  function changeStatus(resource, newStatus) {
    selection.clear()
    router.patch(`/admin/resources/${resource.id}/status`, { status: newStatus })
  }

  function changeAccess(resource, newAccess) {
    selection.clear()
    router.patch(`/admin/resources/${resource.id}/access`, { access: newAccess })
  }

  let pageProps = {
    links: [{ text: "Admin", href: "/admin" }],
    pageTitle: "Resources",
    title: "Resources",
    subtitle: "Manage your downloadable resources",
    Icon: FileDown
  }
</script>

<Page {...pageProps}>

  {#snippet buttons()}
    <Button text="Public page" path="/resources" BeforeIcon={ChevronLeft} />
    <Button text="New resource" Icon={CirclePlus} iconProps={{ size: 12, class: "text-gray-500" }} path="/admin/resources/new" />
  {/snippet}

  <FilterBar {pagination} paginationProps={{ summary: true, terms: ["resource", "resources"] }}>
    
    {#snippet filters()}

      <!-- Status filter -->
      <Dropdown buttonProps={{ text: status.label || "Status", size: "md", Icon: status.icon }} width="16rem" align="start" itemSize="sm">
        {#snippet contents(item)}
          {#each status.items as statusItem (statusItem.value)}
            {@render item({ text: statusItem.label, icon: statusItem.icon, onSelect: () => { selection.clear(); status.setValue(statusItem.value) } })}
          {/each}
        {/snippet}
      </Dropdown>

      <!-- Access filter -->
      <Dropdown buttonProps={{ text: access.label || "Access", size: "md", Icon: access.icon }} width="16rem" align="start" itemSize="sm">
        {#snippet contents(item)}
          {#each access.items as accessItem (accessItem.value)}
            {@render item({ text: accessItem.label, icon: accessItem.icon, onSelect: () => { selection.clear(); access.setValue(accessItem.value) } })}
          {/each}
        {/snippet}
      </Dropdown>

      <Table.SelectedActions {selection} path="resources" />
      
    {/snippet}
    
  </FilterBar>

  <Table.Root empty={resources.length === 0} emptyMessage="No resources found">
    
    <Table.Head>
      <Table.SelectionDropdownCell {selection} />
      <Table.Header width="30%">Name</Table.Header>
      <Table.Header width="25%">Resource</Table.Header>
      <Table.Header width="15%" align="center">Cover image</Table.Header>
      <Table.Header width="12%" align="center">Status</Table.Header>
      <Table.Header width="12%" align="center">Access</Table.Header>
    </Table.Head>

    <Table.Body>
      {#each resources as resource (resource.id)}
        <Table.Row>

          <!-- Checkbox -->
          <Table.Cell width="3rem" align="center">
            <input type="checkbox" checked={selection.has(resource.id)} onclick={e => selection.toggle(resource.id, e)} />
          </Table.Cell>

          <!-- Name -->
          <Table.Cell width="30%">
            <div class="flex items-center gap-2 group">
              <ColorIndicator color={resource.color} />
              <Button variant="link" class="text-gray-800" path={`/admin/resources/${resource.id}/edit`}>{resource.name}</Button>
              <Button variant="naked" Icon={ExternalLink} hint="View" path={`/resources/${resource.id}`} class="text-gray-400 hover:text-gray-700 opacity-0 group-hover:opacity-100 transition-opacity" iconProps={{ size: 12 }} />
              <Button variant="naked" Icon={Pencil} hint="Edit" class="text-gray-400 hover:text-gray-700 opacity-0 group-hover:opacity-100 transition-opacity" iconProps={{ size: 12 }} path={`/admin/resources/${resource.id}/edit`} />
              <Button variant="naked" Icon={Trash2} hint="Delete" class="text-gray-400 hover:text-gray-700 opacity-0 group-hover:opacity-100 transition-opacity" iconProps={{ size: 12 }} onclick={() => deleteResource(resource)} />
            </div>
            {#if resource.tags?.length > 0}
              <div class="flex flex-wrap gap-1 mt-1">
                {#each resource.tags as tag (tag.id)}
                  <Tag {tag} size="xxs" variant="faint" />
                {/each}
              </div>
            {/if}
          </Table.Cell>

          <!-- Resource (file or URL) -->
          <Table.Cell width="25%">
            {#if resource.url}
              <div class="flex items-center gap-2">
                <Link size={14} strokeWidth={1.5} class="text-gray-400 shrink-0" />
                <a href={resource.url} target="_blank" rel="noopener noreferrer" class="text-xs text-gray-600 hover:underline truncate max-w-[200px]">{resource.url}</a>
              </div>
            {:else}
              <FileDetails file={resource.file} />
            {/if}
          </Table.Cell>

          <!-- Cover image -->
          <Table.Cell width="15%" align="center">
            {#if resource.cover_image}
              <Button variant="naked" onclick={() => showImagePreview(resource)}>
                <Image src={resource.cover_image.thumb_url} alt={resource.cover_image.name} class="rounded-none w-[30px] h-[30px] object-cover" />
              </Button>
            {:else}
              <span>-</span>
            {/if}
          </Table.Cell>

          <!-- Status -->
          <Table.Cell width="12%" align="center">
            {#if resource.active}
              <Button variant="primary" size="xs" text="Active" Icon={CheckCircle} hint="Click to make inactive" onclick={() => changeStatus(resource, "inactive")} />
            {:else}
              <Button variant="secondary" size="xs" text="Hidden" Icon={EyeOff} iconClass="text-gray-400" hint="Click to make active" onclick={() => changeStatus(resource, "active")} />
            {/if}
          </Table.Cell>

          <!-- Access -->
          <Table.Cell width="12%" align="center">
            <Dropdown
              width="12rem"
              buttonProps={{
                text: resource.free ? "Free" : "Premium",
                variant: resource.free ? "confirm" : "primary",
                size: "xs",
                disabled: !resource.active,
                hint: !resource.active ? "Resource currently hidden" : null
              }}>
              {#snippet contents(item)}
                {@render item({ text: "Free", icon: Unlock, onSelect: () => changeAccess(resource, "free") })}
                {@render item({ text: "Premium", icon: Lock, onSelect: () => changeAccess(resource, "premium") })}
              {/snippet}
            </Dropdown>
          </Table.Cell>

        </Table.Row>
      {/each}
    </Table.Body>
    
  </Table.Root>

  <Table.Pagination {pagination} paginationProps={{ summary: true, terms: ["resource", "resources"] }} />
  
</Page>
<ImagePreview
<ImagePreview bind:open={imagePreview.open} src={imagePreview.src} index={imagePreview.index} onClose={() => imagePreview.open = false} />